import React, {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react';
import {Invoice, InvoiceFilter} from '../../types/Invoice/Invoice';
import PaginationProvider from '../../components/table/PaginationContext';
import {searchDelay} from '../../services/ApiClient';
import {StateFilter} from '../../components/filter/GenericTableFilters';
import moment from 'moment';
import InvoiceService from '../../services/InvoiceService';
import {PageMetadata} from '../../types/Page';

export const InvoiceListDataContext = createContext<{
  invoices: Invoice[];
  filters: {
    stateFilter: StateFilter[];
    setStateFilter: Dispatch<SetStateAction<StateFilter[]>>;
    dateStartFilter: string;
    setDateStartFilter: Dispatch<SetStateAction<string>>;
    dateEndFilter: string;
    setDateEndFilter: Dispatch<SetStateAction<string>>;
    used: boolean;
    searchFilter: string;
    setSearchFilter: Dispatch<SetStateAction<string>>;
  } | null;
  page: PageMetadata | undefined;
  reload: () => void;
  loading: boolean;
  isPrefilteredForDebtor: boolean;
  currentPage: { set: (page: number) => void; get: number };
  pageSize: { set: (size: number) => void; get: number };
}>({
  invoices: [] as Invoice[],
  filters: null,
  page: {} as PageMetadata,
  reload: () => {},
  loading: true,
  isPrefilteredForDebtor: false,
  currentPage: {
    set: (page) => {},
    get: 0,
  },
  pageSize: {
    set: (size) => {},
    get: 0,
  },
});

type InvoiceListDataContextProviderProps = {
  pageSizeOptions: number[];
  children: any;
  customerId?: string;
  setInvoicesCount?: (invoiceCount: number) => void;
  hasPaginationProvider?: boolean;
};

const InvoiceListDataContextProvider = ({
  pageSizeOptions,
  customerId,
  children,
  setInvoicesCount,
  hasPaginationProvider = true,
}: InvoiceListDataContextProviderProps) => {
  const [invoiceList, setInvoiceList] = useState<Invoice[]>([] as Invoice[]);
  const [page, setPage] = useState<PageMetadata>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [stateFilter, setStateFilter] = useState<StateFilter[]>([] as StateFilter[]);
  const [dateStartFilter, setDateStartFilter] = useState<string>('');
  const [dateEndFilter, setDateEndFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [filtersUsed, setFiltersUsed] = useState(false);

  const isInitialized = React.useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    let delaySearch: any;
    if (isInitialized.current) {
      if (currentPage !== 0) {
        setCurrentPage(0);
      } else {
        delaySearch = setTimeout(() => {
          reload();
        }, searchDelay);
      }
    }
    return () => {
      delaySearch && clearTimeout(delaySearch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, dateStartFilter, dateEndFilter, searchFilter, pageSize]);

  useEffect(() => {
    reload();
    isInitialized.current = true;
    return () => {
      isInitialized.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    setLoading(true);
    const filter: InvoiceFilter = {};
    if (!!stateFilter) {
      filter.statuses = Array.from(stateFilter.values()).join(',');
    }
    filter.search = searchFilter;
    filter.startDate = dateStartFilter && moment(dateStartFilter).format('YYYY-MM-DDT00:00:00.1') + 'Z';
    filter.endDate = dateEndFilter && moment(dateEndFilter).format('YYYY-MM-DDT23:59:59.999') + 'Z';

    setFiltersUsed(stateFilter.length !== 0 || dateStartFilter !== '' || dateEndFilter !== '' || searchFilter !== '');

    if (!!customerId) {
      InvoiceService.getInvoicesForCustomer(customerId, pageSize, currentPage, 'createdDateTimeUTC,desc')
        .then((response) => {
          if (response.results) {
            setInvoiceList(response.results as Invoice[]);
            setInvoicesCount && setInvoicesCount(response.page.totalElements);
          } else {
            setInvoiceList([] as Invoice[]);
          }
          setPage(response?.page);
          setLoading(false);
        })
        .catch((err) => {
          console.error('error', err);
          //TODO handle Error
        });
    } else {
      InvoiceService.getInvoices(pageSize, currentPage, 'desc', filter)
        .then((response) => {
          if (response && response.results) {
            setInvoiceList(response.results as Invoice[]);
            setPage(response?.page);
          } else {
            setInvoiceList([] as Invoice[]);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('error', err);
          //TODO handle Error
        });
    }
  };

  return (
    <InvoiceListDataContext.Provider
      value={{
        invoices: invoiceList,
        filters: {
          stateFilter: stateFilter,
          setStateFilter: setStateFilter,
          dateStartFilter: dateStartFilter,
          setDateStartFilter: setDateStartFilter,
          dateEndFilter: dateEndFilter,
          setDateEndFilter: setDateEndFilter,
          searchFilter: searchFilter,
          setSearchFilter: setSearchFilter,
          used: filtersUsed,
        },
        page: page,
        reload: reload,
        loading: loading,
        isPrefilteredForDebtor: !!customerId,
        currentPage: { set: setCurrentPage, get: currentPage },
        pageSize: { set: setPageSize, get: pageSize },
      }}
    >
      {hasPaginationProvider ? (
        <PaginationProvider
          pageSizeOptions={pageSizeOptions}
          currentPage={currentPage || 0}
          totalPages={page?.totalPages || 0}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          changePageSize={setPageSize}
        >
          {children}
        </PaginationProvider>
      ) : (
        children
      )}
    </InvoiceListDataContext.Provider>
  );
};

export default InvoiceListDataContextProvider;
